// src/components/NavBar.js

import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import { ReactComponent as MenuIcon } from "../icons/menu.svg";
import { ReactComponent as StarIcon } from "../icons/star.svg";
import { ReactComponent as FilledStarIcon } from "../icons/filledstar.svg";
import { ReactComponent as GabonIcon } from "../icons/ga.svg";
import { ReactComponent as BackIcon } from "../icons/cancel.svg";
import { ReactComponent as EnglandIcon } from "../icons/unitedkingdom.svg";
import { ReactComponent as SearchIcon } from "../icons/search.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CountrySelector from "./CountrySelector";
import { db } from "../firebase-config";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { FavoritesContext } from "../context/FavoritesContext";
import Favorites from "./Favorites";

const NavBar = () => {
  const { t, i18n } = useTranslation();
  const [padding, setPadding] = useState("10px 20px");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [favoritesDrawerOpen, setFavoritesDrawerOpen] = useState(false);
  const [countrySelectorOpen, setCountrySelectorOpen] = useState(false);
  const drawerRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [iconSize, setIconSize] = useState({
    height: window.innerWidth < 768 ? "14px" : "16px",
    width: window.innerWidth < 768 ? "14px" : "16px",
  });

  const { getFavoritesCount } = useContext(FavoritesContext);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [allProducts, setAllProducts] = useState([]); // Cache all products for faster search
  const searchInputRef = useRef();
  const debounceTimeoutRef = useRef(null);

  // Adjust padding and icon size based on window width
  useEffect(() => {
    const handleResize = () => {
      setPadding(window.innerWidth >= 768 ? "10px 40px" : "10px 20px");
      setIconSize({
        height: window.innerWidth < 768 ? "13px" : "16px",
        width: window.innerWidth < 768 ? "13px" : "16px",
      });
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setDrawerOpen(false);
      }
    };

    if (drawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [drawerOpen]);

  // Set language based on localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage === "en" ? "en" : "fr");
    }
  }, [i18n]);

  const handleBrandClick = () => {
    if (location.pathname === "/products") {
      window.location.reload();
    } else {
      window.location.href = "/products";
    }
  };

  // Fetch categories from Firestore sorted by createdAt descending
  useEffect(() => {
    setIsLoading(true);
    const q = query(collection(db, "categories"), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedCategories = querySnapshot.docs.map((doc) => doc.id);
        setCategories(fetchedCategories);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching categories:", error);
        setStatusMessage("Failed to fetch categories.");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Fetch and cache all products when categories are loaded
  useEffect(() => {
    const fetchAllProducts = async () => {
      if (categories.length === 0) return;

      try {
        const products = [];

        for (const category of categories) {
          const itemsCollection = collection(db, "products", category, "items");
          const productsSnapshot = await getDocs(itemsCollection);

          productsSnapshot.forEach((doc) => {
            const productData = doc.data();

            // Skip products with visibility "No"
            if (productData.categories?.[0]?.visibility === "No") {
              return;
            }

            products.push({
              id: doc.id,
              name: productData.name || "",
              brandName: productData.brandName || "",
              category: category,
              colors: productData.colors || [],
            });
          });
        }

        setAllProducts(products);
      } catch (error) {
        console.error("Error fetching products for search:", error);
      }
    };

    fetchAllProducts();
  }, [categories]);

  const handleCategoryClick = (category) => {
    setDrawerOpen(false);
    const categoryPath = `/${category.toLowerCase().replace(/\s+/g, "-")}`;
    navigate(categoryPath);
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language === "en" ? "en" : "fr");
  };

  // Live search function - filters cached products
  const performSearch = useCallback(
    (query) => {
      if (query.trim() === "") {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const searchLower = query.toLowerCase().trim();
      const results = [];

      // Search through cached products
      allProducts.forEach((product) => {
        // Search in product name - prioritize names that START with the query
        if (product.name?.toLowerCase().startsWith(searchLower)) {
          results.push({
            type: "product",
            name: product.name,
            category: product.category,
            id: product.id,
            priority: 1, // Higher priority for starts-with matches
          });
        } else if (product.name?.toLowerCase().includes(searchLower)) {
          results.push({
            type: "product",
            name: product.name,
            category: product.category,
            id: product.id,
            priority: 2, // Lower priority for contains matches
          });
        }

        // Search in brand name
        if (product.brandName?.toLowerCase().startsWith(searchLower)) {
          results.push({
            type: "brand",
            name: product.brandName,
            productName: product.name,
            category: product.category,
            id: product.id,
            priority: 1,
          });
        } else if (product.brandName?.toLowerCase().includes(searchLower)) {
          results.push({
            type: "brand",
            name: product.brandName,
            productName: product.name,
            category: product.category,
            id: product.id,
            priority: 2,
          });
        }

        // Search in color names
        if (product.colors && product.colors.length > 0) {
          product.colors.forEach((color) => {
            if (color.name?.toLowerCase().startsWith(searchLower)) {
              results.push({
                type: "color",
                name: color.name,
                productName: product.name,
                category: product.category,
                id: product.id,
                priority: 1,
              });
            } else if (color.name?.toLowerCase().includes(searchLower)) {
              results.push({
                type: "color",
                name: color.name,
                productName: product.name,
                category: product.category,
                id: product.id,
                priority: 2,
              });
            }
          });
        }
      });

      // Search in category names
      categories.forEach((category) => {
        if (category.toLowerCase().startsWith(searchLower)) {
          results.push({
            type: "category",
            name: category,
            priority: 1,
          });
        } else if (category.toLowerCase().includes(searchLower)) {
          results.push({
            type: "category",
            name: category,
            priority: 2,
          });
        }
      });

      // Remove duplicates and sort by priority (starts-with first)
      const uniqueResults = results
        .filter(
          (result, index, self) =>
            index ===
            self.findIndex(
              (r) => r.type === result.type && r.name === result.name
            )
        )
        .sort((a, b) => a.priority - b.priority);

      setSearchResults(uniqueResults.slice(0, 10));
      setIsSearching(false);
    },
    [allProducts, categories]
  );

  // Debounced search - triggers as user types
  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Don't search if query is empty or search is closed
    if (!searchOpen || searchQuery.trim() === "") {
      setSearchResults([]);
      return;
    }

    // Start searching after a minimum of 1 character
    if (searchQuery.trim().length >= 1) {
      setIsSearching(true);

      // Debounce the search by 200ms to avoid too many searches while typing
      debounceTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 200);
    }

    // Cleanup timeout on unmount or when query changes
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchQuery, searchOpen, performSearch]);

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchIconClick = () => {
    setSearchOpen(!searchOpen);
    if (!searchOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
    } else {
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  const handleResultClick = (result) => {
    // Navigate based on search result type
    if (result.type === "brand" || result.type === "color") {
      navigate("/search-item", {
        state: {
          searchType: result.type,
          searchValue: result.name,
        },
      });
    } else if (result.type === "category") {
      navigate("/search-item", {
        state: {
          searchType: "category",
          searchValue: result.name,
        },
      });
    } else if (result.type === "product") {
      // Navigate directly to the product page
      window.location.href = `/full-product?category=${encodeURIComponent(
        result.category
      )}&productId=${encodeURIComponent(result.id)}`;
    }

    // Close search
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const styles = {
    navContainer: {
      background: "white",
      color: "black",
      padding: padding,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: window.innerWidth < 768 ? "30px" : "50px",
      position: "sticky",
      top: "var(--shipping-banner-height, 0px)",
      zIndex: 100,
    },
    searchBarContainer: {
      display: "flex",
      alignItems: "center",
      animation: "expandSearch 0.3s ease-in-out",
      width: "300px",
      maxWidth: "80vw",
    },
    searchInput: {
      flex: 1,
      border: "none",
      outline: "none",
      borderBottom: "1px solid #707070",
      fontFamily: "Didot Bold",
      fontSize: "14px",
      padding: "5px 0",
      color: "#707070",
    },
    searchActions: {
      display: "flex",
      alignItems: "center",
      marginLeft: "10px",
    },
    searchResultsContainer: {
      position: "absolute",
      top: "100%",
      right: 0,
      backgroundColor: "white",
      border: "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: "4px",
      marginTop: "10px",
      width: "300px",
      maxWidth: "80vw",
      maxHeight: "400px",
      overflowY: "auto",
      zIndex: 1002,
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    searchResultItem: {
      padding: "12px 16px",
      cursor: "pointer",
      borderBottom: "1px solid #f0f0f0",
      fontFamily: "Georgia",
      fontSize: "13px",
      backgroundColor: "white",
      transition: "background-color 0.15s ease",
    },
    searchResultType: {
      fontSize: "10px",
      color: "#999",
      textTransform: "uppercase",
      marginBottom: "4px",
    },
    searchingIndicator: {
      padding: "12px 16px",
      fontFamily: "Georgia",
      fontSize: "13px",
      color: "#999",
      textAlign: "center",
    },
    noResults: {
      padding: "12px 16px",
      fontFamily: "Georgia",
      fontSize: "13px",
      color: "#999",
      textAlign: "center",
    },
    iconContainer: {
      display: "flex",
      alignItems: "center",
      position: "relative",
    },
    icon: {
      ...iconSize,
      cursor: "pointer",
    },
    brandTextContainer: {
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
    brandText: {
      fontWeight: "bold",
      fontSize: "18px",
      fontFamily: "Didot Bold",
      color: "#000",
      opacity: "0.7",
      cursor: "pointer",
    },
    drawer: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "250px",
      height: "100%",
      backgroundColor: "white",
      borderRight: "1px solid rgba(0, 0, 0, 0.1)",
      transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
      transition: "transform 0.3s ease",
      zIndex: 1001,
      padding: "20px",
    },
    drawerWide: {
      width: "350px",
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: drawerOpen ? "rgba(0, 0, 0, 0.5)" : "transparent",
      transition: "background-color 0.3s ease",
      zIndex: 1000,
    },
    drawerHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
    },
    drawerContent: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    },
    link: {
      fontFamily: "Georgia",
      color: "#707070",
      opacity: 0.9,
      fontSize: "13px",
      marginTop: "10px",
      cursor: "pointer",
      textDecoration: "none",
    },
    category: {
      fontFamily: "Georgia",
      opacity: 1,
      marginTop: "20px",
      fontWeight: "500",
      fontSize: "20px",
    },
    categoryLink: {
      fontFamily: "Georgia",
      color: "#707070",
      opacity: 0.9,
      fontSize: "13px",
      marginTop: "10px",
      cursor: "pointer",
      paddingLeft: "20px",
    },
    favoritesCount: {
      backgroundColor: "#000",
      color: "white",
      borderRadius: "50%",
      width: "16px",
      height: "16px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      position: "absolute",
      top: "-8px",
      right: "-8px",
      fontSize: "10px",
    },
    highlightedText: {
      fontWeight: "bold",
    },
  };

  // Function to highlight matching text
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    const searchLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    const startIndex = textLower.indexOf(searchLower);

    if (startIndex === -1) return text;

    const endIndex = startIndex + query.length;

    return (
      <>
        {text.substring(0, startIndex)}
        <span style={styles.highlightedText}>
          {text.substring(startIndex, endIndex)}
        </span>
        {text.substring(endIndex)}
      </>
    );
  };

  return (
    <>
      <nav style={styles.navContainer}>
        <MenuIcon style={styles.icon} onClick={() => setDrawerOpen(true)} />
        <div style={styles.brandTextContainer}>
          <span style={styles.brandText} onClick={handleBrandClick}>
            LIJEUL
          </span>
        </div>
        <div style={styles.iconContainer}>
          {!searchOpen && (
            <>
              <SearchIcon style={styles.icon} onClick={handleSearchIconClick} />
              <div style={{ position: "relative", marginLeft: "15px" }}>
                <StarIcon
                  style={styles.icon}
                  onClick={() => setFavoritesDrawerOpen(true)}
                />
                {getFavoritesCount() > 0 && (
                  <div style={styles.favoritesCount}>{getFavoritesCount()}</div>
                )}
              </div>
              <GabonIcon
                style={{
                  ...styles.icon,
                  marginLeft: "15px",
                  borderRadius: "50%",
                }}
              />
            </>
          )}

          {searchOpen && (
            <div style={styles.searchBarContainer}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder={t("Search")}
                style={styles.searchInput}
                autoComplete="off"
              />
              <div style={styles.searchActions}>
                <BackIcon style={styles.icon} onClick={handleSearchIconClick} />
              </div>
            </div>
          )}

          {searchOpen && searchQuery.trim().length > 0 && (
            <div style={styles.searchResultsContainer}>
              {isSearching ? (
                <div style={styles.searchingIndicator}>{t("...")}</div>
              ) : searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <div
                    key={`${result.type}-${result.name}-${index}`}
                    style={styles.searchResultItem}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "white")
                    }
                  >
                    <div style={styles.searchResultType}>
                      {result.type === "product" && t("Produit")}
                      {result.type === "brand" && t("Marque")}
                      {result.type === "category" && t("Catégorie")}
                      {result.type === "color" && t("Couleur")}
                    </div>
                    <div>{highlightMatch(result.name, searchQuery)}</div>
                    {result.productName && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          marginTop: "2px",
                        }}
                      >
                        {t("dans")} {result.productName}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={styles.noResults}>{t("Aucun résultat trouvé")}</div>
              )}
            </div>
          )}
        </div>
      </nav>
      {drawerOpen && (
        <div style={styles.overlay} onClick={() => setDrawerOpen(false)}></div>
      )}
      <div
        style={{
          ...styles.drawer,
          ...(window.innerWidth >= 768 ? styles.drawerWide : {}),
        }}
        ref={drawerRef}
      >
        <div style={styles.drawerHeader}>
          <BackIcon style={styles.icon} onClick={() => setDrawerOpen(false)} />
        </div>
        <div style={styles.drawerContent}>
          <a href="/about-us" style={styles.link}>
            {t("About Us")}
          </a>
          <a href="/contact-us" style={styles.link}>
            {t("Contact Us")}
          </a>
          <div style={styles.category}>-{t("Categories")}-</div>
          {isLoading ? (
            <div className="spinner"></div>
          ) : (
            categories.map((category) => (
              <div
                style={styles.categoryLink}
                key={category}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </div>
            ))
          )}
        </div>
      </div>
      <Favorites
        drawerOpen={favoritesDrawerOpen}
        setDrawerOpen={setFavoritesDrawerOpen}
      />
      {countrySelectorOpen && (
        <CountrySelector
          onClose={() => setCountrySelectorOpen(false)}
          onLanguageChange={handleLanguageChange}
        />
      )}
      {statusMessage && <div className="status-message">{statusMessage}</div>}
    </>
  );
};

export default NavBar;
